import { TECMO_TEAMS } from '../constants/teams'

const TEAMS_BY_ABBREVIATION = [...TECMO_TEAMS].sort((a, b) =>
  a.abbreviation.localeCompare(b.abbreviation)
)

/**
 * Franchise picker with Unknown as the empty value — no ROM team, no helmet.
 *
 * @remarks
 * Unknown is a real choice for results that never recorded a franchise, not a
 * placeholder for "not yet picked". The empty string is what the forms persist
 * as `null` on the way to the API.
 *
 * @example
 * ```tsx
 * <TeamSelect value={draft.homeTeam} onChange={next => onChange({ homeTeam: next })} />
 * ```
 */
export function TeamSelect({
  value,
  onChange,
  className,
  labels = 'full',
  id,
  'aria-label': ariaLabel,
}: {
  value: string
  onChange: (next: string) => void
  className?: string
  /** `full` is `BUF — Buffalo`; `abbr` is just `BUF`. */
  labels?: 'full' | 'abbr'
  id?: string
  'aria-label'?: string
}) {
  const teams = labels === 'abbr' ? TEAMS_BY_ABBREVIATION : TECMO_TEAMS

  return (
    <select
      id={id}
      className={['select', className].filter(Boolean).join(' ')}
      value={value}
      onChange={event => onChange(event.target.value)}
      aria-label={ariaLabel}
    >
      <option value="">Unknown</option>
      {teams.map(team => (
        <option key={team.abbreviation} value={team.abbreviation}>
          {labels === 'full' ? `${team.abbreviation} — ${team.name}` : team.abbreviation}
        </option>
      ))}
    </select>
  )
}
